import React, { useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, DownloadIcon, LayoutGridIcon, ListIcon, MapPinIcon, SearchIcon, SearchXIcon, UserPlusIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { FilterSelect } from '../components/ui/FilterSelect';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { departmentNames, divisionNames, locationNames, statusNames } from '../data/org';
import { useHRStore } from '../hooks/useHRStore';
import type { Employee, Navigate, RoleSegment } from '../types/hr';
import { displayName } from '../utils/employees';
import { segmentById } from '../utils/teams';
import { formatDate } from '../utils/format';
type SortKey = 'name' | 'employeeNumber' | 'jobTitle' | 'department' | 'division' | 'location' | 'status' | 'hireDate';
type SortDir = 'asc' | 'desc';
const COLUMNS: {
  key: SortKey;
  label: string;
}[] = [{
  key: 'name',
  label: 'Name'
}, {
  key: 'employeeNumber',
  label: 'Employee #'
}, {
  key: 'jobTitle',
  label: 'Job Title'
}, {
  key: 'department',
  label: 'Department'
}, {
  key: 'division',
  label: 'Division'
}, {
  key: 'location',
  label: 'Location'
}, {
  key: 'status',
  label: 'Status'
}, {
  key: 'hireDate',
  label: 'Hire Date'
}];
const ALL = 'All';
export function Employees({
  onNavigate,
  segment
}: {
  onNavigate: Navigate;
  segment?: RoleSegment;
}) {
  const {
    employees
  } = useHRStore();
  const [roleSegment, setRoleSegment] = useState<RoleSegment | undefined>(segment);
  const activeSegment = segmentById(roleSegment);
  const [query, setQuery] = useState('');
  const [department, setDepartment] = useState(ALL);
  const [division, setDivision] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [location, setLocation] = useState(ALL);
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [sort, setSort] = useState<{
    key: SortKey;
    dir: SortDir;
  }>({
    key: 'employeeNumber',
    dir: 'asc'
  });
  const deptCount = new Set(employees.map(e => e.department)).size;
  const divCount = new Set(employees.map(e => e.division)).size;
  const locCount = new Set(employees.map(e => e.location)).size;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = employees.filter(e => (!activeSegment || activeSegment.test(e)) && (department === ALL || e.department === department) && (division === ALL || e.division === division) && (status === ALL || e.status === status) && (location === ALL || e.location === location) && (!q || `${displayName(e)} ${e.jobTitle} ${e.employeeNumber} ${e.workEmail}`.toLowerCase().includes(q)));
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => sortValue(a, sort.key).localeCompare(sortValue(b, sort.key)) * dir);
  }, [employees, query, department, division, status, location, sort, activeSegment]);
  const hasFilters = Boolean(query) || Boolean(activeSegment) || [department, division, status, location].some(v => v !== ALL);
  const clearFilters = () => {
    setRoleSegment(undefined);
    setQuery('');
    setDepartment(ALL);
    setDivision(ALL);
    setStatus(ALL);
    setLocation(ALL);
  };
  const toggleSort = (key: SortKey) => setSort(prev => prev.key === key ? {
    key,
    dir: prev.dir === 'asc' ? 'desc' : 'asc'
  } : {
    key,
    dir: 'asc'
  });
  const open = (id: string) => onNavigate({
    page: 'profile',
    employeeId: id
  });
  return <div data-id="element-1234" data-mp="3aqnxsbpjbbah">
      <PageHeader eyebrow="People" title="Employees" description={`${employees.length} people across ${deptCount} departments, ${divCount} divisions and ${locCount} locations`} actions={<>
            <Button icon={DownloadIcon} onClick={() => toast.success('Roster export started', {
        description: `${filtered.length} employees · CSV`
      })} data-id="element-1236" data-mp="31q7wn8w9kg8s">
              Export
            </Button>
            <Button variant="primary" icon={UserPlusIcon} onClick={() => toast('Add employee', {
        description: 'New hires are created from the onboarding flow in Salesforce.'
      })} data-id="element-1237" data-mp="19nn6n90rfpqp">
              Add employee
            </Button>
          </>} data-id="element-1235" data-mp="3ar5tn78abnhv" />

      <div className="rounded-2xl bg-white shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-1238" data-mp="lyu9584hp9tz">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center" data-id="element-1239" data-mp="qxnluybycwt3">
          <div className="relative flex-1 lg:max-w-xs" data-id="element-1240" data-mp="cy5qg045ilar">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-1241" data-mp="czqm5t27caa7" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name, title, #…" aria-label="Search employees" className="h-10 w-full rounded-xl border-0 bg-canvas pl-10 pr-3 text-sm text-navy ring-1 ring-inset ring-slate-200 placeholder:text-mutedlight focus:bg-white focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-1242" data-mp="27c6owjvsh6ab" />
          </div>
          <div className="grid flex-1 grid-cols-2 gap-2 md:grid-cols-4" data-id="element-1243" data-mp="2v93c2q97d7ku">
            <FilterSelect label="Department" allLabel="All departments" value={department} options={departmentNames} onChange={setDepartment} data-id="element-1244" data-mp="2qez5w97rh3kt" />
            <FilterSelect label="Division" allLabel="All divisions" value={division} options={divisionNames} onChange={setDivision} data-id="element-1245" data-mp="306lm6jyyhsuf" />
            <FilterSelect label="Status" allLabel="All statuses" value={status} options={statusNames} onChange={setStatus} data-id="element-1246" data-mp="3e536s8tkep84" />
            <FilterSelect label="Location" allLabel="All locations" value={location} options={locationNames} onChange={setLocation} data-id="element-1247" data-mp="3a5at5hpbdsvw" />
          </div>
          <div className="flex items-center gap-2" data-id="element-1248" data-mp="1fr04pacmt0qn">
            {hasFilters && <button type="button" onClick={clearFilters} className="px-2 text-xs font-medium text-azure hover:underline" data-id="element-1249" data-mp="3ev9ru6p3x560">
                Clear
              </button>}
            <div className="flex rounded-xl bg-canvas p-1 ring-1 ring-inset ring-slate-200" role="group" aria-label="View" data-id="element-1250" data-mp="3jw64b8nixzn1">
              {([{
              id: 'table',
              icon: ListIcon,
              label: 'Table view'
            }, {
              id: 'cards',
              icon: LayoutGridIcon,
              label: 'Card view'
            }] as const).map(({
              id,
              icon: Icon,
              label
            }) => <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} aria-label={label} className={`flex h-8 w-8 items-center justify-center rounded-lg transition ${view === id ? 'bg-white text-navy shadow-sm' : 'text-muted hover:text-navy'}`} data-id="element-1251" data-mp="6lqa2jzn2jiq">
                  <Icon className="h-4 w-4" data-id="element-1252" data-mp="pc7wk6tvoivn" />
                </button>)}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-3 text-xs text-muted" data-id="element-1253" data-mp="25u8dmxupc2nr">
          <span className="flex flex-wrap items-center gap-2" data-id="element-1254" data-mp="20vexexo15xjw">
            <span data-id="element-1255" data-mp="20vx30kwl1o4h">
              Showing <span className="font-semibold text-navy" data-id="element-1256" data-mp="1vwk7lbrnb8qa">{filtered.length}</span> of {employees.length}
            </span>
            {activeSegment && <span className="inline-flex items-center gap-1.5 rounded-full bg-navy px-2.5 py-0.5 text-[11px] font-medium text-white" data-id="element-1257" data-mp="2tjswzhurp1xk">
                Role: {activeSegment.label}
                <button type="button" onClick={() => setRoleSegment(undefined)} aria-label={`Remove ${activeSegment.label} filter`} className="rounded-full hover:text-flame" data-id="element-1258" data-mp="2jthhcy1q14au">
                  <XIcon className="h-3 w-3" data-id="element-1259" data-mp="3cbbz7mgywbyj" />
                </button>
              </span>}
          </span>
          <span className="hidden sm:inline" data-id="element-1260" data-mp="37j2dyjqss02l">Click a row to open the profile</span>
        </div>

        {filtered.length === 0 ? <div className="px-6 pb-6" data-id="element-1261" data-mp="2fv7qjhumcs47">
            <EmptyState icon={SearchXIcon} title="No employees match these filters" description="Try a different search or clear filters to see everyone." action={<Button size="sm" onClick={clearFilters} data-id="element-1263" data-mp="2ayhauvmq7yjb">
                  Clear filters
                </Button>} data-id="element-1262" data-mp="2kmz8j3zplpiq" />
          </div> : view === 'table' ? <div className="overflow-x-auto" data-id="element-1264" data-mp="33md57totpecx">
            <table className="w-full min-w-[1000px] text-left text-sm" data-id="element-1265" data-mp="33lbr1gcigbqz">
              <thead data-id="element-1266" data-mp="2plk76yzbc152">
                <tr className="border-y border-slate-100 bg-canvas/70" data-id="element-1267" data-mp="2pm2dypjlpxr8">
                  {COLUMNS.map(c => {
                const active = sort.key === c.key;
                const SortIcon = active ? sort.dir === 'asc' ? ArrowUpIcon : ArrowDownIcon : ArrowUpDownIcon;
                return <th key={c.key} scope="col" aria-sort={active ? sort.dir === 'asc' ? 'ascending' : 'descending' : 'none'} className="px-6 py-3" data-id="element-1268" data-mp="1j3x3otned3g9">
                        <button type="button" onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider transition ${active ? 'text-navy' : 'text-muted hover:text-navy'}`} data-id="element-1269" data-mp="16mmdkm4xthiw">
                          {c.label}
                          <SortIcon className={`h-3.5 w-3.5 ${active ? 'text-azure' : 'text-slate-300'}`} aria-hidden="true" data-id="element-1270" data-mp="n0g8ood5wqkf" />
                        </button>
                      </th>;
              })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1271" data-mp="98a47zjdg80t">
                {filtered.map(e => <tr key={e.id} tabIndex={0} onClick={() => open(e.id)} onKeyDown={ev => {
              if (ev.key === 'Enter') open(e.id);
            }} className="cursor-pointer transition-colors hover:bg-azure/[0.04] focus:bg-azure/[0.06] focus:outline-none" data-id="element-1272" data-mp="496vo546njo3">
                    <td className="px-6 py-3.5" data-id="element-1273" data-mp="1n1pyqi4rjdtp">
                      <div className="flex items-center gap-3" data-id="element-1274" data-mp="1n0y2d53cdc2d">
                        <Avatar name={displayName(e)} seed={e.id} size="sm" data-id="element-1275" data-mp="1j0e7k868qkje" />
                        <div className="min-w-0" data-id="element-1276" data-mp="1e95wl7p9ir44">
                          <p className="truncate font-medium text-navy" data-id="element-1277" data-mp="1e8e2607eizff">{displayName(e)}</p>
                          <p className="truncate text-xs text-muted" data-id="element-1278" data-mp="19aumcvv22eld">{e.workEmail.split('@')[0]}@…</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 font-mono text-xs text-slate-500" data-id="element-1279" data-mp="zc53p0vc4q42">{e.employeeNumber}</td>
                    <td className="px-6 py-3.5 text-slate-700" data-id="element-1280" data-mp="va1rga0udhhq">{e.jobTitle}</td>
                    <td className="px-6 py-3.5 text-slate-700" data-id="element-1281" data-mp="36y6j7kqreoom">{e.department}</td>
                    <td className="px-6 py-3.5 text-slate-600" data-id="element-1282" data-mp="3az8l69c29eua">{e.division}</td>
                    <td className="px-6 py-3.5 text-slate-600" data-id="element-1283" data-mp="3b1bjdgndaea3">{e.location}</td>
                    <td className="px-6 py-3.5" data-id="element-1284" data-mp="3fyuwuec8qn0z">
                      <StatusBadge status={e.status} data-id="element-1285" data-mp="3kx672q6g6zjq" />
                    </td>
                    <td className="px-6 py-3.5 text-slate-600" data-id="element-1286" data-mp="3kv25iew0ucmm">{formatDate(e.hireDate)}</td>
                  </tr>)}
              </tbody>
            </table>
          </div> : <div className="grid grid-cols-1 gap-4 px-4 pb-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-id="element-1287" data-mp="2s6eioevmjf6y">
            {filtered.map(e => <button key={e.id} type="button" onClick={() => open(e.id)} className="group flex flex-col rounded-2xl bg-white p-5 text-left ring-1 ring-slate-200/80 transition hover:-translate-y-0.5 hover:shadow-lift hover:ring-azure/30" data-id="element-1288" data-mp="31zlsqd1b7jdd">
                <div className="flex items-start justify-between" data-id="element-1289" data-mp="21g0j5kjxtfgt">
                  <Avatar name={displayName(e)} seed={e.id} size="lg" data-id="element-1290" data-mp="1xgrp0ekzjfkq" />
                  <StatusBadge status={e.status} data-id="element-1291" data-mp="2b7d7i26j1ni3" />
                </div>
                <p className="mt-4 font-semibold text-navy group-hover:text-azure" data-id="element-1292" data-mp="2b6lgl4nvkdy5">{displayName(e)}</p>
                <p className="text-sm text-slate-600" data-id="element-1293" data-mp="26ad2zvpbsst3">{e.jobTitle}</p>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-100 pt-3 text-xs text-muted" data-id="element-1294" data-mp="1ijh4ygua11de">
                  <span data-id="element-1295" data-mp="1eix698u21pp5">{e.department}</span>
                  <span className="inline-flex items-center gap-1" data-id="element-1296" data-mp="1sher8yskso3d">
                    <MapPinIcon className="h-3 w-3" aria-hidden="true" data-id="element-1297" data-mp="1seisx9ctfxee" />
                    {e.location}
                  </span>
                  <span data-id="element-1298" data-mp="33viu6uh55mkk">Since {formatDate(e.hireDate, 'MMM yyyy')}</span>
                </div>
              </button>)}
          </div>}
      </div>
    </div>;
}
function sortValue(e: Employee, key: SortKey): string {
  if (key === 'name') return `${e.lastName} ${e.firstName}`;
  return String(e[key]);
}